/**
 * Trainer dashboard — real /api/cases data.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Mast } from "../components/Mast.js";
import { api } from "../lib/api.js";

interface Case {
  id: string;
  trainer_id: string;
  case_user_id: string | null;
  name: string;
  birth_date: string | null;
  created_at: string;
}

export default function TrainerDashboard() {
  const [cases, setCases]   = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  async function load() {
    setLoading(true);
    const r = await api<{ ok: true; cases: Case[] }>("/api/cases");
    setCases(r.cases);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  return (
    <>
      <Mast section="Trainer · cases" />
      <main className="page">
        <section style={{ marginBottom: "var(--s-9)" }}>
          <div className="serial rise rise-1" style={{ marginBottom: "var(--s-3)" }}>
            ROSTER · {cases.length} 位
          </div>
          <h1 className="rise rise-2" style={{ maxWidth: 760 }}>
            訓練師工作台 — <span className="italic-display" style={{ color: "var(--rust)" }}>個案 ＆ 會次</span> 的安靜中樞。
          </h1>
          <p className="rise rise-3 muted" style={{ marginTop: "var(--s-4)", fontSize: "var(--t-md)", maxWidth: 580, lineHeight: 1.6 }}>
            建立個案、排訓練、寫筆記。在開始 session 之前你看見的是視訊，
            開始之後 — 螢幕變成遊戲、攝影機留在角落、訊號在中央。
          </p>
          <div className="row rise rise-4" style={{ marginTop: "var(--s-6)", gap: "var(--s-3)" }}>
            <button className="btn btn--rust" onClick={() => setShowModal(true)}>＋ 建立個案</button>
            <button
              className="btn btn--ghost"
              onClick={() => {
                if (cases.length === 0) {
                  setShowModal(true);
                  return;
                }
                document.getElementById("roster-list")?.scrollIntoView({
                  behavior: "smooth", block: "start",
                });
              }}>
              選個案排訓練 ↓
            </button>
            <Link to="/dashboard/presets" className="btn btn--ghost" style={{ textDecoration: "none" }}>
              ⚙ Protocol presets
            </Link>
          </div>
        </section>

        <section id="roster-list" className="rise rise-5">
          <div className="section-head">
            <div>
              <div className="eyebrow">Cases</div>
              <h2>我的個案</h2>
              <p className="muted" style={{ fontSize: "var(--t-xs)", marginTop: 4, lineHeight: 1.5 }}>
                點選個案 → 在個案頁面按「＋ 排定 session」即可排新訓練。
              </p>
            </div>
            <input type="text" placeholder="搜尋個案名 / ID …" style={{ maxWidth: 280, fontSize: "var(--t-sm)" }} />
          </div>

          {loading ? (
            <div className="muted">載入中…</div>
          ) : cases.length === 0 ? (
            <div className="empty">
              <div className="italic-display" style={{ fontSize: "var(--t-xl)", marginBottom: "var(--s-2)" }}>
                還沒有個案
              </div>
              <div className="muted" style={{ marginBottom: "var(--s-4)" }}>建立第一位個案開始</div>
              <button className="btn btn--rust" onClick={() => setShowModal(true)}>＋ 建立個案</button>
            </div>
          ) : (
            <div className="list">
              <div className="list-row" style={{
                borderBottom: "1px solid var(--ink)",
                paddingBottom: "var(--s-2)",
                marginBottom: "var(--s-1)",
              }}>
                <div className="serial">姓名</div>
                <div className="serial">出生日期</div>
                <div className="serial">建立日</div>
                <div className="serial" style={{ textAlign: "right" }}>狀態</div>
              </div>
              {cases.map((cs) => (
                <Link key={cs.id} to={`/dashboard/cases/${cs.id}`}
                      style={{ border: "none", color: "inherit", display: "block" }}>
                  <div className="list-row">
                    <div>
                      <div className="list-row__name">{cs.name}</div>
                      <div className="list-row__meta">{cs.id.slice(0, 12)}</div>
                    </div>
                    <div className="muted tabular">{cs.birth_date ?? "—"}</div>
                    <div className="muted tabular">{cs.created_at.slice(0, 10)}</div>
                    <div style={{ textAlign: "right" }}>
                      {cs.case_user_id
                        ? <span className="chip chip--olive">activated</span>
                        : <span className="chip chip--petrol">intake</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>

      {showModal && <CaseCreateModal onClose={() => setShowModal(false)} onCreated={load} />}
    </>
  );
}

function CaseCreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: "", birth_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await api("/api/cases", { method: "POST", body: form });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? "Failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onSubmit={submit} onClick={(e) => e.stopPropagation()}>
        <div className="serial" style={{ marginBottom: "var(--s-2)" }}>New case</div>
        <h3 className="modal__title">建立新個案</h3>
        <div className="modal__sub">基本資料先填，session 排程下一步做。</div>

        <div className="col" style={{ gap: "var(--s-4)" }}>
          <div className="field">
            <label className="field-label">姓名 / 暱稱</label>
            <input required autoFocus value={form.name}
                   onChange={e => setForm({ ...form, name: e.target.value })}
                   placeholder="例：張小明" />
          </div>
          <div className="field">
            <label className="field-label">出生日期 (選填)</label>
            <input type="date" value={form.birth_date}
                   onChange={e => setForm({ ...form, birth_date: e.target.value })} />
          </div>
          <div className="field">
            <label className="field-label">備註</label>
            <textarea value={form.notes}
                      onChange={e => setForm({ ...form, notes: e.target.value })}
                      placeholder="主訴、目標、注意事項…" />
          </div>
          {error && <div className="alert">{error}</div>}
        </div>

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>取消</button>
          <button type="submit" className="btn btn--rust" disabled={busy}>
            {busy ? "建立中…" : "建立"}
          </button>
        </div>
      </form>
    </div>
  );
}
